import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { BeamTrussArrow } from '../../types/beamTruss';
import { getLoadVisualScale } from '@/utils/gridScale';

interface BeamTrussArrowProps {
  position: THREE.Vector3;
  length: number; // Changed from size to length
  color: string;
  direction?: [number, number, number];
  scaleFactor?: number;
}

interface BeamTrussIndicatorProps {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  arrowCount: number;
  arrowLength: number; // Changed from arrowSize to arrowLength
  color: string;
  visible?: boolean;
  scaleFactor?: number;
}

// Individual arrow component with line and arrowhead
const TrussArrow: React.FC<BeamTrussArrowProps> = ({ 
  position, 
  length, 
  color, 
  direction = [0, -1, 0],
  scaleFactor = 1
}) => {
  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);
  
  const { arrowLine, arrowHead } = useMemo(() => {
    const dir = new THREE.Vector3(...direction).normalize();
    const arrowStart = position.clone();
    const arrowEnd = position.clone().add(dir.clone().multiplyScalar(length));
    
    // Create arrowhead (small cone at the end) - scaled based on model scale
    const coneRadius = 0.015 * visualScale;
    const coneHeight = 0.04 * visualScale;
    const arrowHeadGeometry = new THREE.ConeGeometry(coneRadius, coneHeight, 8);
    
    // Z-UP: Rotate cone to point in the specified direction
    const rotationMatrix = new THREE.Matrix4();
    const up = new THREE.Vector3(0, 0, 1);  // Z-UP convention
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(up, dir);
    rotationMatrix.makeRotationFromQuaternion(quaternion);
    arrowHeadGeometry.applyMatrix4(rotationMatrix);
    
    return {
      arrowLine: [arrowStart, arrowEnd],
      arrowHead: { geometry: arrowHeadGeometry, position: arrowEnd }
    };
  }, [position, length, direction, visualScale]);

  return (
    <group>
      {/* Arrow line */}
      <Line
        points={arrowLine}
        color={color}
        lineWidth={1}
      />
      {/* Arrow head */}
      <mesh position={arrowHead.position} geometry={arrowHead.geometry}>
        <meshStandardMaterial color={color} />
      </mesh>
      {/* Arrow head outline */}
      <mesh position={arrowHead.position} geometry={arrowHead.geometry} scale={[1.1, 1.1, 1.1]}>
        <meshBasicMaterial color="black" wireframe transparent opacity={0.6} />
      </mesh>
    </group>
  );
};

// Main beam truss indicator component
export const BeamTrussIndicator: React.FC<BeamTrussIndicatorProps> = ({
  startPoint,
  endPoint,
  arrowCount,
  arrowLength,
  color,
  visible = true,
  scaleFactor = 1
}) => {
  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);
  
  const { arrows, baseLine } = useMemo(() => {
    if (!visible || arrowCount <= 0) return { arrows: [], baseLine: null };

    const beamVector = endPoint.clone().sub(startPoint);
    const beamLength = beamVector.length();
    
    if (beamLength === 0) return { arrows: [], baseLine: null };

    // Calculate beam-only endpoints (excluding nodes)
    const nodeRadius = 0.05 * visualScale; // Scale node radius
    const beamDirection = beamVector.clone().normalize();
    
    // Adjust start and end points to stop at beam edges, not node centers
    const adjustedStartPoint = startPoint.clone().add(beamDirection.clone().multiplyScalar(nodeRadius));
    const adjustedEndPoint = endPoint.clone().sub(beamDirection.clone().multiplyScalar(nodeRadius));

    const arrows: BeamTrussArrow[] = [];
    
    // Z-UP: Calculate offset above the beam for the base line
    const offsetDistance = 0.05 * visualScale; // Scale offset
    const upVector = new THREE.Vector3(0, 0, 1);  // Z-UP convention
    
    // Calculate perpendicular vector pointing upward
    const perpendicular = new THREE.Vector3().crossVectors(beamDirection, upVector).normalize();
    if (perpendicular.length() === 0) {
      // If beam is vertical (parallel to Z-axis), use X-axis
      perpendicular.set(1, 0, 0);
    }
    
    // Create base line points using adjusted beam endpoints (parallel to beam, offset above by arrow length + spacing)
    const lineOffset = offsetDistance + arrowLength; // Position line above arrows
    const offsetVector = new THREE.Vector3(0, lineOffset, 0);
    const baseLineStart = adjustedStartPoint.clone().add(offsetVector);
    const baseLineEnd = adjustedEndPoint.clone().add(offsetVector);
    
    // Generate evenly spaced arrows hanging down from the base line
    for (let i = 0; i <= arrowCount; i++) {
      const t = i / arrowCount;
      const position = baseLineStart.clone().lerp(baseLineEnd, t);
      
      arrows.push({
        position: {
          x: position.x,
          y: position.y,
          z: position.z
        },
        size: arrowLength, // This is now length, not size
        direction: [0, -1, 0] // Downward from the base line
      });
    }
    
    return {
      arrows,
      baseLine: [baseLineStart, baseLineEnd]
    };
  }, [startPoint, endPoint, arrowCount, arrowLength, visible, visualScale]);

  if (!visible) return null;

  return (
    <group>
      {/* Base line connecting all arrows */}
      {baseLine && (
        <Line
          points={baseLine}
          color={color}
          lineWidth={2}
        />
      )}
      
      {/* Individual arrows */}
      {arrows.map((arrow, index) => (
        <TrussArrow
          key={index}
          position={new THREE.Vector3(arrow.position.x, arrow.position.y, arrow.position.z)}
          length={arrow.size} // Using size as length now
          color={color}
          direction={arrow.direction}
          scaleFactor={scaleFactor}
        />
      ))}
    </group>
  );
};
